import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const BarcodeHistoryList = ({ barcodes, onDeactivate, onRefresh }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [deactivating, setDeactivating] = useState(null);

  const handleDeactivate = async (barcodeId) => {
    if (!confirm('Are you sure you want to deactivate this barcode?')) return;

    setDeactivating(barcodeId);
    try {
      await onDeactivate(barcodeId);
    } finally {
      setDeactivating(null);
    }
  };

  const filteredBarcodes = barcodes?.filter(barcode => {
    const matchesSearch = 
      barcode?.user_profiles?.full_name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      barcode?.user_profiles?.email?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      barcode?.barcode_data?.toLowerCase()?.includes(searchQuery?.toLowerCase());

    const matchesStatus = 
      filterStatus === 'all' ||
      (filterStatus === 'active' && barcode?.is_active) ||
      (filterStatus === 'inactive' && !barcode?.is_active);

    return matchesSearch && matchesStatus;
  });

  return (
    <div className="bg-card rounded-lg border border-border">
      <div className="p-6 border-b border-border">
        <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
          <h2 className="text-xl font-bold">Barcode History</h2>
          <div className="flex gap-3 w-full md:w-auto">
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              placeholder="Search members..."
              className="md:w-64"
            />
            <Button
              onClick={onRefresh}
              variant="outline"
              size="icon"
              iconName="RefreshCw"
            />
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 mt-4">
          {['all', 'active', 'inactive']?.map(status => (
            <button
              key={status}
              onClick={() => setFilterStatus(status)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filterStatus === status
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              {status?.charAt(0)?.toUpperCase() + status?.slice(1)}
            </button>
          ))}
        </div>
      </div>
      <div className="overflow-x-auto">
        {filteredBarcodes?.length === 0 ? (
          <div className="p-12 text-center">
            <Icon name="Search" size={48} className="mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">
              {searchQuery ? 'No barcodes found matching your search' : 'No barcodes generated yet'}
            </p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-muted">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Member
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Barcode ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Scan Count
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Generated
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredBarcodes?.map(barcode => (
                <tr key={barcode?.id} className="hover:bg-muted/50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden">
                        {barcode?.user_profiles?.avatar_url ? (
                          <img
                            src={barcode?.user_profiles?.avatar_url}
                            alt={barcode?.user_profiles?.full_name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <Icon name="User" size={20} className="text-primary" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-foreground">
                          {barcode?.user_profiles?.full_name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {barcode?.user_profiles?.email}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <p className="font-mono text-xs break-all max-w-xs">
                      {barcode?.barcode_data}
                    </p>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm capitalize">
                      {barcode?.barcode_type?.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                        barcode?.is_active
                          ? 'bg-success/10 text-success' :'bg-destructive/10 text-destructive'
                      }`}
                    >
                      <Icon name={barcode?.is_active ? 'CheckCircle' : 'XCircle'} size={12} />
                      {barcode?.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm">{barcode?.scan_count || 0}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm">
                      {new Date(barcode?.generated_at)?.toLocaleDateString()}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {barcode?.is_active && (
                      <Button
                        onClick={() => handleDeactivate(barcode?.id)}
                        variant="outline"
                        size="sm"
                        loading={deactivating === barcode?.id}
                        iconName="XCircle"
                      >
                        Deactivate
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default BarcodeHistoryList;